/**
 * CPV My actions' door to the server — every CPV act waiting on the signed-in person (GET /bp/cpv/my-actions/?kind=&page=&page_size=).
 *
 * One page `{counts: {KIND: n}, results, count, page, page_size, pages}`, sized by the server's `cpv.api.page_size`
 * unless the caller names a size; the rows come in the server's order (overdue first, then the earliest due). The
 * client only words the closed vocabularies and opens the object's own CPV page — it never acts from a row.
 */
import { getJson } from './cpvApi';
import { PLANT_KEY, masterApi } from './cpvMasterApi';

export const myActionsApi = {
  list: (params) => getJson('/cpv/my-actions/', params),
};

/** The kinds in display order (engines/vocabulary/cpv.py MyActionKind). */
export const KIND_WORD = {
  SHEET_ENTRY: 'Data entry',
  SHEET_VERIFY: 'Verification',
  SHEET_APPROVE: 'Sheet approval',
  CORRECTION_APPROVE: 'Correction approval',
  MASTER_REVIEW: 'Parameter review',
  MASTER_APPROVE: 'Parameter approval',
  PLAN_REVIEW: 'Plan review',
  PLAN_APPROVE: 'Plan approval',
  SIGNAL_ASSESS: 'Signal assessment',
  COUNTERSIGN: 'Countersign',
  REPORT_REVIEW: 'Report review',
  REPORT_APPROVE: 'Report approval',
  REPORT_RETURNED: 'Report returned',
  HISTORY_VERIFY: 'History import verification',
  INSIGHT_REVIEW: 'Insight review',
  AUDIT_TRAIL_REVIEW: 'Audit trail review',
  BATCH_FACT_VERIFY: 'Batch details verification',
};
export const KINDS = Object.keys(KIND_WORD);

export const OBJECT_WORD = { CPV_SHEET: 'Data sheet', CPV_MASTER: 'CPV parameters', CPV_PLAN: 'CPV plan', CPV_REPORT: 'CPV report', CPV_SIGNAL: 'Signal',
  CPV_HISTORY_IMPORT: 'History import', CPV_INSIGHT: 'Insight', CPV_AUDIT_TRAIL: 'CPV audit trail', CPV_BATCH: 'Batch' };

/** The total waiting on the person: the sum of the server's counts by kind. */
export const totalOf = (counts) => Object.values(counts || {}).reduce((t, n) => t + (Number(n) || 0), 0);

const word = (code) => (code ? String(code).replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase()) : '');
/** The acts the row offers, worded ("Approve · Return"). */
export const actsOf = (row) => [...new Set((row?.allowed_acts || []).map((a) => word(a.act)))].join(' · ');
/** The e-signature meanings the row's signed acts bind (the server's words): an act signed under one of several meanings,
 *  chosen in its dialog (a sheet approval names the batch disposition), lists those (`esign_meanings`). */
export const meaningsOf = (row) => [...new Set((row?.allowed_acts || []).filter((a) => a.esign_required)
  .flatMap((a) => (a.esign_meanings?.length ? a.esign_meanings : [a.esign_meaning])).filter(Boolean))];

/** The object's CPV page, when the row alone names it (a master row names its version: see openMyAction). */
export function myActionPath(row) {
  const id = encodeURIComponent(row?.object_id ?? '');
  switch (row?.object_type) {
    case 'CPV_SHEET':
      // a correction row names its sheet too (detail_id is the proposed entry); a sheet approval row opens the approve
      // dialog on the sheet page (the same dialog as the page's Approve: disposition chosen inside the approval)
      return row?.kind === 'SHEET_APPROVE' ? `/cpv/sheets/${id}?act=approve` : `/cpv/sheets/${id}`;
    case 'CPV_PLAN':
      return `/cpv/plans/${id}`;
    case 'CPV_REPORT':
      return `/cpv/reports/${id}`;
    case 'CPV_SIGNAL':
      return `/cpv/signals/${id}`;
    case 'CPV_HISTORY_IMPORT':
      return `/cpv/batches/imports/${id}`;   // G-09: the import page, where the second person verifies
    case 'CPV_INSIGHT':
      return `/cpv/insights?insight=${id}`;   // AI-5: the Insights page opens the insight's drawer
    case 'CPV_AUDIT_TRAIL':
      return '/cpv/audit-trail';             // Q12-i: the plant's overdue review (object id: the plant)
    case 'CPV_MASTER':
      return '/cpv/master';
    case 'CPV_BATCH':
      return `/cpv/batches/${id}`;           // batch details awaiting verification: the batch page's Batch details card
    default:
      return null;
  }
}

/** Open the row's object. A master row names a version: its product and number are read from the version itself
 *  (GET /bp/cpv/master/versions/<id>/), so the master page opens on that version; unreadable → the masters register. */
export async function openMyAction(row, navigate) {
  if (row?.object_type === 'CPV_MASTER') {
    try {
      const v = await masterApi.version(row.object_id);
      navigate(`/cpv/master/${encodeURIComponent(v?.product_id ?? PLANT_KEY)}?version=${encodeURIComponent(v?.version ?? '')}`);
    } catch {
      navigate('/cpv/master');
    }
    return;
  }
  const path = myActionPath(row);
  if (path) navigate(path);
}
